/**
 * Global entry for reusable ofcs classes and helpers
 *
 * @module
 */

import { Model } from 'backbone';
import Cache from './ofcs/cache';
import Collection from './ofcs/collection';
import Contour from './ofcs/contour';
import createContainer from './ofcs/createContainer';
import Distance from './ofcs/distance';
import EventEmitter from './ofcs/eventEmitter';
import Interface from './ofcs/interface';
import Line from './ofcs/line';
import Bounds from './ofcs/bounds';
import Logger from './ofcs/logger';
import namespace from './ofcs/namespace';
import OfcsObject from './ofcs/object';
import Path from './ofcs/path';
import Point from './ofcs/point';
import View from './ofcs/view';
import Matrix from './ofcs/matrix';
import Draggable from './ofcs/draggable';
import LayoutUpdateTracker from './ofcs/layoutUpdateTracker';
import memoizedDebounce from './ofcs/memoizedDebounce';
import KeyBinder from './ofcs/keyBinder';
import LengthUnit from './ofcs/unit/lengthUnit';
import MeasurementsConverter from './ofcs/unit/measurementsConverter';
import loadScript from './loadScript';
import mountMicroFrontend from './mountMicroFrontend';

/**
 * Create default container
 * @type {Pimple}
 */
export const container = createContainer();

export {
    createContainer,
    Logger,
    namespace,
    View,
    Model,
    Collection,
    Contour,
    Cache,
    OfcsObject,
    EventEmitter,
    Path,
    Point,
    Distance,
    Line,
    Bounds,
    Interface,
    Matrix,
    Draggable,
    LayoutUpdateTracker,
    memoizedDebounce,
    KeyBinder,
    LengthUnit,
    MeasurementsConverter,
    loadScript,
    mountMicroFrontend,
};

/**
 * Ofcs singleton, holds the container
 *
 * @class Ofcs
 * @singleton
 */
const Ofcs = window.Ofcs || {};

window.Ofcs = Object.assign(Ofcs || {}, {
    Cache,
    Logger,
    namespace,
    createContainer,
    container,
    View,
    Model,
    Collection,
    Object: OfcsObject,
    Point,
    Bounds,
    Distance,
    Line,
    Matrix,
    Interface,
    Draggable,
    LayoutUpdateTracker,
    KeyBinder,

    /**
     * Convenient alias for Ofcs#namespace
     *
     * @method
     * @member Ofcs
     * @alias Ofcs#namespace
     */
    ns: namespace,

    Unit: {
        LengthUnit,
        MeasurementsConverter,
    },
});

/**
 * *Models* are the heart of any JavaScript application, containing the interactive
 * data as well as a large part of the logic surrounding it: conversions, validations,
 * computed properties, and access control. You extend *Backbone.Model* with your
 * domain-specific methods, and *Model* provides a basic set of functionality for
 * managing changes.
 *
 * The following is a contrived example, but it demonstrates defining a model with a
 * custom method, setting an attribute, and firing an event keyed to changes in that
 * specific attribute. After running this code once, sidebar will be available in your
 * browser's console, so you can play around with it.
 *
 *     @example
 *     const Sidebar = Backbone.Model.extend({
 *         promptColor: function() {
 *             const cssColor = prompt("Please enter a CSS color:");
 *             this.set({color: cssColor});
 *         }
 *     });
 *
 *     window.sidebar = new Sidebar;
 *
 *     sidebar.on('change:color', function(model, color) {
 *         $('#sidebar').css({background: color});
 *     });
 *
 *     sidebar.set({color: 'white'});
 *
 *     sidebar.promptColor();
 *
 * @class   Backbone.Model
 * @extends Backbone.Events
 */

/**
 * Contains default values for #attributes
 *
 * @protected
 * @property {Object} defaults
 */

/**
 * Contains all model attributes
 *
 * @protected
 * @property {Object} attributes
 */

/**
 * Called when the attribute `property` is changed.
 * @event change:property
 * @param {Backbone.Model} model Model which property was changed
 * @param {*} value New value of the property
 */

/**
 * Called when the some attribute is changed.
 * @event change
 * @param {Backbone.Model} model Model which property was changed
 * @param {Object} options Object with event options
 * @param {Object} options.changes Object with a *Boolean* `true` for each changed property
 */

/**
 * When creating an instance of a model, you can pass in the initial values of the
 * *attributes*, which will be #set on the model. If you define an *initialize* function,
 * it will be invoked when the model is created.
 *
 *     new Book({
 *         title: "One Thousand and One Nights",
 *         author: "Scheherazade"
 *     });
 *
 * @constructor
 */

/**
 * Set a hash of attributes (one or many) on the model. If any of the attributes
 * change the models state, a "change" event will be triggered, unless `{silent: true}`
 * is passed as an option. Change events for specific attributes are also triggered,
 * and you can bind to those as well, for example: `change:title`, and `change:content`.
 * You may also pass individual keys and values.
 *
 *     note.set({title: "March 20", content: "In his eyes she eclipses..."});
 *
 *     book.set("title", "A Scandal in Bohemia");
 *
 * If the model has a #validate method, it will be isValid before the attributes are
 * set, no changes will occur if the validation fails, and #set will return `false`.
 * Otherwise, #set returns a reference to the model. You may also pass an `error` callback
 * in the options, which will be invoked instead of triggering an `"error"` event, should
 * validation fail. If `{silent: true}` is passed as an option, the validation is deferred
 * until the next change.
 *
 * @method set
 * @param {Object} attributes Key value pairs of attributes, or attribute key
 * @param {Object} [options] Optional attribute value if a key is passed into first param
 */

/**
 * Events is a module that can be mixed in to any object, giving the object the ability
 * to bind and trigger custom named events. Events do not have to be declared before
 * they are bound, and may take passed arguments. For example:
 *
 *     @example
 *     const object = {};
 *
 *     _.extend(object, Backbone.Events);
 *
 *     object.on("alert", function(msg) {
 *       alert("Triggered " + msg);
 *     });
 *
 *     object.trigger("alert", "an event");
 *
 * @class Backbone.Events
 */

/**
 * Bind a callback function to an object. The callback will be invoked whenever
 * the event is fired. If you have a large number of different events on a page,
 * the convention is to use colons to namespace them: `"poll:start"`, or
 * `"change:selection"`. The event string may also be a space-delimited list of
 * several events...
 *
 *     book.on("change:title change:author", ...);
 *
 * To supply a context value for this when the callback is invoked, pass the
 * optional third argument: `model.on('change', this.render, this);`
 *
 * Callbacks bound to the special "all" event will be triggered when any event
 * occurs, and are passed the name of the event as the first argument. For example,
 * to proxy all events from one object to another:
 *
 *     proxy.on("all", function(eventName) {
 *         object.trigger(eventName);
 *     });
 *
 * @method on
 * @param {String} event Event Name
 * @param {Function} callback Callback function
 * @param {*} [context] Event context
 */

/**
 * Remove a previously-bound callback function from an object. If no context
 * is specified, all of the versions of the callback with different contexts
 * will be removed. If no callback is specified, all callbacks for the event
 * will be removed. If no event is specified, all event callbacks on the object
 * will be removed.
 *
 *     // Removes just the `onChange` callback.
 *     object.off("change", onChange);
 *
 *     // Removes all "change" callbacks.
 *     object.off("change");
 *
 *     // Removes the `onChange` callback for all events.
 *     object.off(null, onChange);
 *
 *     // Removes all callbacks for `context` for all events.
 *     object.off(null, null, context);
 *
 *     // Removes all callbacks on `object`.
 *     object.off();
 *
 * @method off
 * @param  {String} [event] Event Name
 * @param  {Function} [callback] Callback function
 * @param  {*} [context] Callback function
 */

/**
 * Trigger callbacks for the given event, or space-delimited list of events.
 * Subsequent arguments to trigger will be passed along to the event callbacks.
 *
 * @method trigger
 * @param {String} event Event Name
 * @param {...*} [args] Arguments passed to the callbacks
 */
